<script setup lang="ts">
import { ApiGetInteractionEventPage } from "@/apis/interaction";
import { InteractionEventType } from "@/enums/mission";
import { InteractionEvent } from "@/types/entity";
import { computed, nextTick } from "vue";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import ThreadList, { ThreadListInstance } from "../mobile/ThreadList.vue";
import { watch } from "vue";
// Constants
const PER_PAGE = 10;

// Props
const props = defineProps<{
  enablePullLoad: boolean;
  sourceMateId: number;
  targetMateId: number;
}>();

// States
const { t } = useI18n();
const activeTab = ref(0);

const tabs = computed<
  {
    title: string;
    type: InteractionEventType[];
  }[]
>(() => [
  {
    title: t("interactive.reviewPage.history.tabs.all"),
    type: [],
  },
  {
    title: t("interactive.reviewPage.history.tabs.read"),
    type: [
      InteractionEventType.ReceivedLetter,
      InteractionEventType.LetterSent,
    ],
  },
  {
    title: t("interactive.reviewPage.history.tabs.mission"),
    type: [
      InteractionEventType.MissionReturned,
      InteractionEventType.LeftMission,
      InteractionEventType.MissionInvitationApproved,
      InteractionEventType.MissionInvitationRejected,
      InteractionEventType.MissionInvitationSent,
    ],
  },
  {
    title: t("interactive.reviewPage.history.tabs.diary"),
    type: [InteractionEventType.StrollReturned],
  },
  {
    title: t("interactive.reviewPage.history.tabs.relationship"),
    type: [
      InteractionEventType.FriendInvitationSent,
      InteractionEventType.FriendInvitationApproved,
      InteractionEventType.FriendInvitationRejected,
      InteractionEventType.ReceivedLetterFriendInvitation,
    ],
  },
]);
const allTypes = computed<InteractionEventType[]>(() => {
  return tabs.value.flatMap((tab) => tab.type || []);
});
// List States
const validTypes = computed<InteractionEventType[]>(() => {
  if (activeTab.value === 0) {
    return allTypes.value;
  }
  return tabs.value[activeTab.value].type;
});
const dataList = ref<InteractionEvent[]>([]);
const pageNum = ref<number>(0);
const listRef = ref<ThreadListInstance | null>(null);

// Lifecycle
watch(activeTab, async () => {
  await refresh();
});
async function initOrFetchList(force: boolean = true) {
  // 1. 获取缓存列表
  if (pageNum.value > 0 && !force) {
    return;
  }
  // 2. 获取新列表
  const newPageNum = 1;
  const data = await fetchList(
    props.sourceMateId,
    props.targetMateId,
    validTypes.value,
    newPageNum
  );
  dataList.value = data;
  pageNum.value = newPageNum;
}
async function fetchListNextPage(): Promise<boolean> {
  const data = await fetchList(
    props.sourceMateId,
    props.targetMateId,
    validTypes.value,
    pageNum.value + 1
  );
  if (data.length === 0) {
    return false;
  }
  dataList.value = dataList.value.concat(data);
  pageNum.value = pageNum.value + 1;
  return data.length >= PER_PAGE;
}
async function fetchList(
  sourceMateId: number,
  targetMateId: number,
  types: InteractionEventType[],
  pageNum: number
) {
  const { page } = await ApiGetInteractionEventPage({
    filter: {
      mateId: sourceMateId,
      participantMateId: targetMateId,
      type: types,
    },
    pagination: {
      page: pageNum,
      perPage: PER_PAGE,
    },
    sort: {
      field: "id",
      order: "desc",
    },
  });
  return page.data;
}

const refresh = async () => {
  dataList.value = [];
  pageNum.value = 0;
  await nextTick();
  listRef.value?.resetScrollLoadingState();
};
</script>

<template>
  <div class="h-full grid grid-rows-[auto_1fr] overflow-hidden">
    <VanTabs
      v-model:active="activeTab"
      shrink
      :style="{
        '--van-tabs-nav-background': 'hsl(var(--b2))',
      }"
    >
      <VanTab v-for="tab in tabs" :key="tab.title" :title="tab.title" />
    </VanTabs>
    <ThreadList
      ref="listRef"
      :enable-pull-load="enablePullLoad"
      :on-pull-load="
        async () => {
          await initOrFetchList();
          nextTick(() => listRef?.resetScrollLoadingState());
        }
      "
      :on-scroll-load="() => fetchListNextPage()"
      class="relationship-event-list"
    >
      <div class="px-2">
        <div class="m-2 mr-0 flex flex-col gap-2 border-l-2 px-2">
          <InteractionItem
            v-for="event in dataList"
            :key="event.id"
            :event="event"
            :is-need-action="false"
            class="relative before:content-[''] before:w-2 before:h-2 before:bg-primary before:rounded-full before:block before:absolute before:-left-[13px] before:top-5"
          />
        </div>
      </div>
    </ThreadList>
  </div>
</template>
